import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Plus, Trash2, Check, X, AlertCircle, Save } from "lucide-react";
import { MilestoneFormFields } from "./MilestoneFormFields";
import { VersionDiffViewer } from "./VersionDiffViewer";
import { safeParseLinks } from "./useTimelineManager";
import { LinksConfigSection } from "./ProjectsSubSections";

export interface TimelineEvent {
  id: string;
  type: string;
  year: string;
  company_en: string;
  company_tr: string;
  role_en: string;
  role_tr: string;
  duration: string;
  duration_en: string;
  duration_tr: string;
  details_en: string; // JSON string
  details_tr: string; // JSON string
  logo_url?: string;
  sort_order: number;
  links?: string | any[];
}

interface MilestoneEditFormProps {
  editingEvent: Partial<TimelineEvent>;
  setEditingEvent: React.Dispatch<React.SetStateAction<Partial<TimelineEvent> | null>>;
  detailsEn: string[];
  setDetailsEn: React.Dispatch<React.SetStateAction<string[]>>;
  detailsTr: string[];
  setDetailsTr: React.Dispatch<React.SetStateAction<string[]>>;
  onSave: () => void;
  onCancel: () => void;
  type: string;
  activeDraft?: any;
  onSaveDraft?: () => void;
  onDiscardDraft?: () => void;
  liveMilestone?: any;
}

interface MilestoneBulletsSectionProps {
  detailsEn: string[];
  detailsTr: string[];
  updateDetail: (idx: number, val: string, lang: "en" | "tr") => void;
  addDetailField: (lang: "en" | "tr") => void;
  removeDetailField: (idx: number, lang: "en" | "tr") => void;
}

function MilestoneBulletsSection({
  detailsEn,
  detailsTr,
  updateDetail,
  addDetailField,
  removeDetailField,
}: MilestoneBulletsSectionProps) {
  const maxLen = Math.max(detailsEn.length, detailsTr.length);

  return (
    <div className="space-y-2 pt-4 border-t border-border/40 text-left">
      <div className="flex justify-between items-center mb-1">
        <Label className="font-bold text-xs uppercase tracking-wider text-brand-secondary">
          Details / Bullets Spreadsheet
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            addDetailField("en");
            addDetailField("tr");
          }}
          className="h-7 text-xs border-dashed border-brand-secondary/50 text-brand-secondary hover:text-brand-primary"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Aligned Bullet Row
        </Button>
      </div>

      {maxLen === 0 ? (
        <p className="text-xs italic text-muted-foreground py-4 text-center border border-dashed rounded-xl">
          No details or bullet rows configured yet.
        </p>
      ) : (
        <div className="overflow-x-auto border border-border/60 rounded-xl bg-card/30">
          <table className="min-w-full divide-y divide-border/40 text-sm">
            <thead className="bg-muted/40 font-semibold text-brand-secondary">
              <tr>
                <th className="px-3 py-2 text-center w-16 border-r border-border/40 text-[10px] uppercase tracking-wider">Row</th>
                <th className="px-4 py-2 text-left border-r border-border/40 text-[10px] uppercase tracking-wider">English Bullet (EN)</th>
                <th className="px-4 py-2 text-left border-r border-border/40 text-[10px] uppercase tracking-wider">Turkish Bullet (TR)</th>
                <th className="px-2 py-2 text-center w-16 text-[10px] uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {Array.from({ length: maxLen }).map((_, idx) => (
                <tr key={idx} className="hover:bg-muted/10 transition-colors">
                  <td className="px-3 py-2 border-r border-border/40 text-center font-mono text-xs text-muted-foreground align-middle">
                    #{idx + 1}
                  </td>
                  <td className="px-3 py-1.5 border-r border-border/40">
                    <Textarea
                      value={detailsEn[idx] || ""}
                      onChange={(e) => updateDetail(idx, e.target.value, "en")}
                      className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-8 min-h-[32px] resize-y py-1 text-xs"
                      placeholder={`Bullet #${idx + 1} English`}
                    />
                  </td>
                  <td className="px-3 py-1.5 border-r border-border/40">
                    <Textarea
                      value={detailsTr[idx] || ""}
                      onChange={(e) => updateDetail(idx, e.target.value, "tr")}
                      className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-8 min-h-[32px] resize-y py-1 text-xs"
                      placeholder={`Bullet #${idx + 1} Türkçe`}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-center align-middle">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        removeDetailField(idx, "en");
                        removeDetailField(idx, "tr");
                      }}
                      className="h-8 w-8 text-destructive hover:bg-destructive/10 rounded-lg"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function useMilestoneBullets(
  setDetailsEn: React.Dispatch<React.SetStateAction<string[]>>,
  setDetailsTr: React.Dispatch<React.SetStateAction<string[]>>
) {
  const updateDetail = (idx: number, val: string, lang: "en" | "tr") => {
    if (lang === "en") {
      setDetailsEn((prev) => {
        const next = [...prev];
        while (next.length <= idx) next.push("");
        next[idx] = val;
        return next;
      });
    } else {
      setDetailsTr((prev) => {
        const next = [...prev];
        while (next.length <= idx) next.push("");
        next[idx] = val;
        return next;
      });
    }
  };

  const addDetailField = (lang: "en" | "tr") => {
    if (lang === "en") setDetailsEn((prev) => [...prev, ""]);
    else setDetailsTr((prev) => [...prev, ""]);
  };

  const removeDetailField = (idx: number, lang: "en" | "tr") => {
    if (lang === "en") setDetailsEn((prev) => prev.filter((_, i) => i !== idx));
    else setDetailsTr((prev) => prev.filter((_, i) => i !== idx));
  };

  return { updateDetail, addDetailField, removeDetailField };
}

const getComparisonObject = (event: Partial<TimelineEvent>, detEn: string[], detTr: string[]) => {
  return {
    ...event,
    details_en: JSON.stringify(detEn.filter((d) => d.trim().length > 0)),
    details_tr: JSON.stringify(detTr.filter((d) => d.trim().length > 0)),
    links: typeof event.links === "string" ? event.links : JSON.stringify(event.links || []),
  };
};

const MILESTONE_DIFF_FIELDS = [
  { key: "year", label: "Year" },
  { key: "company_en", label: "English Company/Institution" },
  { key: "company_tr", label: "Turkish Company/Institution" },
  { key: "role_en", label: "English Role/Degree" },
  { key: "role_tr", label: "Turkish Role/Degree" },
  { key: "duration", label: "Duration" },
  { key: "details_en", label: "English Details/Bullets", isLongText: true },
  { key: "details_tr", label: "Turkish Details/Bullets", isLongText: true },
  { key: "links", label: "Reference Links", isLongText: true },
];

export function MilestoneEditForm({
  editingEvent,
  setEditingEvent,
  detailsEn,
  setDetailsEn,
  detailsTr,
  setDetailsTr,
  onSave,
  onCancel,
  type,
  activeDraft,
  onSaveDraft,
  onDiscardDraft,
  liveMilestone,
}: MilestoneEditFormProps) {
  const { updateDetail, addDetailField, removeDetailField } = useMilestoneBullets(setDetailsEn, setDetailsTr);

  const draftComparison = getComparisonObject(editingEvent, detailsEn, detailsTr);
  const liveComparison = liveMilestone
    ? {
      ...liveMilestone,
      details_en: typeof liveMilestone.details_en === "string" ? liveMilestone.details_en : JSON.stringify(liveMilestone.details_en),
      details_tr: typeof liveMilestone.details_tr === "string" ? liveMilestone.details_tr : JSON.stringify(liveMilestone.details_tr),
      links: typeof liveMilestone.links === "string" ? liveMilestone.links : JSON.stringify(liveMilestone.links || []),
    }
    : null;

  return (
    <div className="p-5 border border-border/60 rounded-2xl bg-card/65 space-y-4 max-w-3xl text-left">
      <h5 className="font-bold text-base text-brand-primary">
        {editingEvent.id ? "Edit Milestone" : "Add Milestone"}
      </h5>

      {/* Staging Actions Panel */}
      <div className="flex flex-wrap justify-between items-center bg-muted/15 border border-border/50 rounded-xl p-3 gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground font-semibold">
          <AlertCircle className="h-4.5 w-4.5 text-amber-500" />
          <span>
            {activeDraft
              ? "⚠️ Staged Staging Draft has unpublished modifications."
              : "✅ Working copy is synced to database."}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {activeDraft && onDiscardDraft && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDiscardDraft}
              className="text-destructive border-destructive/20 hover:bg-destructive/5 font-bold h-9 rounded-lg"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Discard Draft
            </Button>
          )}
          {onSaveDraft && (
            <Button
              type="button"
              size="sm"
              onClick={onSaveDraft}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold h-9 rounded-lg"
            >
              <Save className="h-4 w-4 mr-1.5" /> Save as Staging Draft
            </Button>
          )}
        </div>
      </div>

      <MilestoneFormFields editingEvent={editingEvent} setEditingEvent={setEditingEvent} />

      <MilestoneBulletsSection
        detailsEn={detailsEn}
        detailsTr={detailsTr}
        updateDetail={updateDetail}
        addDetailField={addDetailField}
        removeDetailField={removeDetailField}
      />

      <LinksConfigSection
        links={safeParseLinks(editingEvent.links)}
        onChange={(newLinks) => setEditingEvent((prev) => prev ? { ...prev, links: newLinks } : prev)}
      />

      <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
        <Button variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-2" /> Cancel
        </Button>
        <Button onClick={onSave} className="bg-brand-primary text-white">
          <Check className="h-4 w-4 mr-2" /> Publish Live
        </Button>
      </div>

      {liveComparison && (
        <VersionDiffViewer
          live={liveComparison}
          draft={draftComparison}
          fieldsToCompare={MILESTONE_DIFF_FIELDS}
        />
      )}
    </div>
  );
}

